import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./FashionPortfolio.css";
import AudioFloat from "./AudioFloat.js";

/* ---------- Utilities ---------- */
function useIsMobile(query = "(max-width: 767px)") {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = (e) => setIsMobile(e.matches);
    mql.addEventListener ? mql.addEventListener("change", on) : mql.addListener(on);
    return () => {
      mql.removeEventListener ? mql.removeEventListener("change", on) : mql.removeListener(on);
    };
  }, [query]);
  return isMobile;
}

/* Re-run reveal after items/view change; gracefully reveal if IO missing */
function useRevealOnScroll(deps = []) {
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const nodes = Array.from(document.querySelectorAll(".fp-item"));
      if (!nodes.length) return;

      if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
        nodes.forEach((n) => n.classList.add("is-visible"));
        return;
      }

      nodes.forEach((n) => n.classList.remove("is-visible"));

      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("is-visible");
              obs.unobserve(e.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "10% 0% -6% 0%" }
      );

      nodes.forEach((n) => io.observe(n));
    });

    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* Chunk photos into snap pages (4 boxes desktop, 2 mobile). Wide = 2 units. */
function paginateItems(photos, isMobile) {
  const capacity = isMobile ? 2 : 4;
  const pages = [];
  let current = [];
  let used = 0;

  photos.forEach((it) => {
    const isWide = !!(it.fullWidth || it.fullwidth || it.full_width);
    const units = isWide ? 2 : 1;

    if (units > capacity) {
      if (current.length) {
        pages.push(current);
        current = [];
        used = 0;
      }
      pages.push([it]);
      return;
    }

    if (used + units > capacity) {
      pages.push(current);
      current = [it];
      used = units;
    } else {
      current.push(it);
      used += units;
    }
  });

  if (current.length) pages.push(current);
  return pages;
}

/* ---------- Lightbox ---------- */
function Lightbox({ open, items, index, onClose, onPrev, onNext }) {
  const img = items[index];

  const onKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    },
    [open, onClose, onPrev, onNext]
  );

  useEffect(() => {
    if (!open) return;
    document.documentElement.classList.add("no-scroll");
    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onKey]);

  if (!open || !img) return null;

  return (
    <div className="fp-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <button className="fp-lightbox__close" aria-label="Close" onClick={onClose}>×</button>
      <button
        className="fp-lightbox__nav left"
        aria-label="Previous"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        ‹
      </button>
      <div className="fp-lightbox__content" onClick={(e) => e.stopPropagation()}>
        <img className="fp-lightbox__media" src={img.url || img} alt="" />
      </div>
      <button
        className="fp-lightbox__nav right"
        aria-label="Next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        ›
      </button>
    </div>
  );
}

/* ---------- Title/Header ---------- */
const Title = () => (
  <header className="fp-header">
    <h2 className="fp-subtitle"></h2>
    <h1 className="fp-title">Fashion — Through My Lens</h1>
    <p className="fp-inquiry">
      For private shoot inquiries and bespoke commissions,&nbsp;
      <a
        className="fp-link"
        href={
          "mailto:contact@lorranlima.com" +
          "?subject=" +
          encodeURIComponent("Private shoot inquiry — Fashion Portfolio") +
          "&body=" +
          encodeURIComponent(
            "Tell us about the concept, location, timing, and any mood references. What story would you like the images to tell?"
          )
        }
      >
        contact us here
      </a>.
    </p>
  </header>
);

/* ---------- Main ---------- */
export default function FashionPortfolio({
  items = [],
  loading = false,
  error = null,
  fetchAllFashionPhotos,
}) {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "scroll"

  useEffect(() => { fetchAllFashionPhotos?.(); }, [fetchAllFashionPhotos]);

  useRevealOnScroll([items, loading, viewMode]);

  // Lightbox
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const openAt = (i) => { setLbIndex(i); setLbOpen(true); };
  const onClose = () => setLbOpen(false);
  const onPrev = () => setLbIndex((i) => (i - 1 + items.length) % items.length);
  const onNext = () => setLbIndex((i) => (i + 1) % items.length);

  // Hero playlist
  const MOBILE_VIDEOS = ["https://lvltravel.s3.us-east-2.amazonaws.com/fashion/fashionFront.mp4", "https://lvltravel.s3.us-east-2.amazonaws.com/fashion/fashionFrontMobile.mp4"];
  const DESKTOP_VIDEOS = ["https://lvltravel.s3.us-east-2.amazonaws.com/fashion/fashionFront.mp4", "https://lvltravel.s3.us-east-2.amazonaws.com/fashion/fashionFrontDesktop.mp4"];
  const playlist = isMobile ? MOBILE_VIDEOS : DESKTOP_VIDEOS;
  const [vidIndex, setVidIndex] = useState(0);
  const videoRef = useRef(null);
  useEffect(() => { setVidIndex(0); }, [isMobile]);
  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    v.pause(); v.load();
    const t = setTimeout(() => v.play().catch(() => {}), 10);
    return () => clearTimeout(t);
  }, [vidIndex, isMobile]);
  const rotateDesktopFirst = !isMobile && vidIndex === 0;
  const onEnded = () => setVidIndex((i) => (i + 1) % playlist.length);

  // Scroll view pages
  const pages = useMemo(() => paginateItems(items, isMobile), [items, isMobile]);
  const carouselRef = useRef(null);
  const goPage = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const pageW = el.clientWidth;
    const target = Math.round((el.scrollLeft + dir * pageW) / pageW) * pageW;
    el.scrollTo({ left: target, behavior: "smooth" });
  };

  return (
    <div className={`fashion ${viewMode === "scroll" ? "is-scroll" : "is-grid"}`}>
      {/* Hero */}
      <section className={`fp-hero ${rotateDesktopFirst ? "fp-hero--rotate" : ""}`} aria-label="Fashion hero video">
        <video
          ref={videoRef}
          className="fp-hero__video"
          playsInline
          autoPlay
          muted
          loop={playlist.length === 1}
          preload="auto"
          onEnded={playlist.length > 1 ? onEnded : undefined}
        >
          <source src={playlist[vidIndex]} type="video/mp4" />
        </video>
        <div className="fp-hero__veil" />

        {/* UPDATED HERO TITLE: LVL + small all-caps FASHION */}
        <div className="fp-hero__center">
          <div className="fp-hero__stack">
            <div className="fp-hero__logo">LVL</div>
            <div className="fp-hero__kicker">FASHION</div>
          </div>
        </div>

        <button
          className="fp-hero__scroll-cue"
          onClick={() => document.querySelector(viewMode === "scroll" ? ".fp-carousel-wrap" : ".fp-grid-wrap")
            ?.scrollIntoView({ behavior: "smooth" })}
          aria-label="Scroll to portfolio"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
          </svg>
        </button>
      </section>

      {/* Header + View Toggle */}
      <div className="fp-header">
        <Title />

        {/* TOGGLE — frosted pill group */}
        <div className="fp-view-toggle fp-view-toggle--pill" role="tablist" aria-label="View mode">
          <button
            className={`fp-toggle-btn ${viewMode === "grid" ? "is-active" : ""}`}
            role="tab"
            aria-selected={viewMode === "grid"}
            onClick={() => setViewMode("grid")}
          >
            Grid view
          </button>
          <button
            className={`fp-toggle-btn ${viewMode === "scroll" ? "is-active" : ""}`}
            role="tab"
            aria-selected={viewMode === "scroll"}
            onClick={() => setViewMode("scroll")}
          >
            Scroll view
          </button>
        </div>
      </div>

      {/* GRID VIEW */}
      {viewMode === "grid" && (
        <section key="grid" className="fp-grid-wrap">
          <div className="fp-grid">
            {items.map((it, i) => {
              const isWide = !!(it.fullWidth || it.fullwidth || it.full_width);
              return (
                <figure
                  key={it._id || it.id || `${it.url}-${i}`}
                  className={`fp-item ${isWide ? "fp-item--wide" : ""}`}
                >
                  <div className="fp-tile">
                    <img
                      className="fp-img"
                      src={it.url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onClick={() => openAt(i)}
                    />
                  </div>
                </figure>
              );
            })}
          </div>
        </section>
      )}

      {/* SCROLL VIEW */}
      {viewMode === "scroll" && (
        <section key="scroll" className="fp-carousel-wrap">
          <button className="fp-page-nav left" aria-label="Previous group" onClick={() => goPage(-1)}>‹</button>

          <div className="fp-carousel" ref={carouselRef}>
            {pages.map((page, pi) => (
              <div className="fp-page" key={`page-${pi}`}>
                <div className="fp-grid fp-grid--page">
                  {page.map((it, i) => {
                    const isWide = !!(it.fullWidth || it.fullwidth || it.full_width);
                    const absIndex = items.indexOf(it);
                    return (
                      <figure
                        key={it._id || it.id || `${it.url}-${pi}-${i}`}
                        className={`fp-item ${isWide ? "fp-item--wide" : ""}`}
                      >
                        <div className="fp-tile">
                          <img
                            className="fp-img"
                            src={it.url}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            onClick={() => openAt(absIndex)}
                          />
                        </div>
                      </figure>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <button className="fp-page-nav right" aria-label="Next group" onClick={() => goPage(1)}>›</button>

          {/* mobile swipe cue */}
          <div className="fp-swipe-cue" aria-hidden="true">
            <span className="fp-swipe-cue__icon">‹ ›</span>
            <span className="fp-swipe-cue__text">Swipe</span>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <Lightbox
        open={lbOpen}
        items={items}
        index={lbIndex}
        onClose={onClose}
        onPrev={onPrev}
        onNext={onNext}
      />

       <AudioFloat
              srcs={[
                { src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/fashion.mp3", type: "audio/mpeg" },
                
              ]}
              startMuted={false}
              startVolume={0.5}
              loop={true}
              label="Site background audio"
              startTime = {32.5}
              
            />
          
    </div>

    
  );
}
